import React, { useState, useEffect } from 'react';
import { assistantAPI } from '../lib/api';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { BarChart3, Users, MessageCircle, Clock, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';

var WEEKDAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

function StatCard({ icon, label, value, sub }) {
  return (
    <div data-testid={"usage-stat-" + label.toLowerCase().replace(/[^a-z0-9]/g, '-')} className="bg-white border border-slate-200 rounded-lg p-4 flex items-start gap-3">
      <div className="p-2 rounded-md bg-slate-50 text-slate-500">{icon}</div>
      <div>
        <div className="text-2xl font-bold text-slate-800">{value}</div>
        <div className="text-xs text-slate-500">{label}</div>
        {sub ? <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div> : null}
      </div>
    </div>
  );
}

function BarRow({ items, labels, maxVal, colorClass }) {
  if (!maxVal) maxVal = Math.max(1, ...items);
  return (
    <div className="flex items-end gap-1 h-24">
      {items.map(function renderBar(v, i) {
        var pct = maxVal > 0 ? Math.max(2, (v / maxVal) * 100) : 2;
        return (
          <div key={i} className="flex flex-col items-center flex-1 min-w-0">
            <span className="text-[9px] text-slate-400 mb-0.5">{v || ''}</span>
            <div className={"w-full rounded-t " + (colorClass || "bg-indigo-400")}
                 style={{ height: pct + '%', minHeight: '2px', transition: 'height 0.3s ease' }} />
            <span className="text-[9px] text-slate-500 mt-1 truncate w-full text-center">{labels[i]}</span>
          </div>
        );
      })}
    </div>
  );
}

function UserRow({ u, expanded, onToggle }) {
  var bh = u.busiest_hour != null ? u.busiest_hour + ' Uhr' : '\u2014';
  var bw = u.busiest_weekday != null ? WEEKDAYS[u.busiest_weekday] : '\u2014';
  var lastDate = (u.last_at || '').replace('T', ' ').slice(0, 10);
  return (
    <React.Fragment>
      <tr data-testid={"usage-user-row-" + u.user_id}
          className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
          onClick={onToggle}>
        <td className="py-2 px-3 text-sm">
          <span className="inline-flex items-center gap-1">
            {expanded ? <ChevronDown className="h-3 w-3 text-slate-400" /> : <ChevronRight className="h-3 w-3 text-slate-400" />}
            <span className="font-medium text-slate-700">{u.name}</span>
          </span>
        </td>
        <td className="py-2 px-2 text-xs text-slate-500">{u.role || '\u2014'}</td>
        <td className="py-2 px-2 text-sm text-right font-medium">{u.total}</td>
        <td className="py-2 px-2 text-sm text-right">{u.last_7d}</td>
        <td className="py-2 px-2 text-sm text-right">{u.last_30d}</td>
        <td className="py-2 px-2 text-sm text-right">{u.avg_per_active_day}</td>
        <td className="py-2 px-2 text-xs text-center">{bh}</td>
        <td className="py-2 px-2 text-xs text-center">{bw}</td>
        <td className="py-2 px-2 text-xs text-slate-400 text-right">{lastDate}</td>
      </tr>
      {expanded ? (
        <tr>
          <td colSpan={9} className="bg-slate-50 px-4 py-3">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <div className="text-[11px] font-medium text-slate-500 mb-2">Nachrichten nach Wochentag</div>
                <BarRow items={u.by_weekday} labels={WEEKDAYS} colorClass="bg-violet-400" />
              </div>
              <div>
                <div className="text-[11px] font-medium text-slate-500 mb-2">Nachrichten nach Uhrzeit</div>
                <BarRow items={u.by_hour} labels={Array.from({length: 24}, function(_, i) { return i; })} colorClass="bg-sky-400" />
              </div>
            </div>
          </td>
        </tr>
      ) : null}
    </React.Fragment>
  );
}

export function AssistantUsageAnalytics() {
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(true);
  var [error, setError] = useState('');
  var [expandedUser, setExpandedUser] = useState(null);
  var [sortKey, setSortKey] = useState('total');
  var [sortDir, setSortDir] = useState(-1);

  useEffect(function loadAnalytics() {
    setLoading(true);
    assistantAPI.usageAnalytics()
      .then(function onOk(res) { setData(res.data); })
      .catch(function onErr() { setError('Nutzungsdaten konnten nicht geladen werden.'); })
      .finally(function onDone() { setLoading(false); });
  }, []);

  function handleSort(key) {
    if (key === sortKey) {
      setSortDir(function flip(d) { return d * -1; });
    } else {
      setSortKey(key);
      setSortDir(-1);
    }
  }

  if (loading) {
    return (
      <Card>
        <CardHeader><CardTitle className="text-base">KI-Nutzung</CardTitle></CardHeader>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }
  if (error) {
    return (
      <Card>
        <CardHeader><CardTitle className="text-base">KI-Nutzung</CardTitle></CardHeader>
        <CardContent><p className="text-sm text-red-500">{error}</p></CardContent>
      </Card>
    );
  }
  if (!data || !data.users || data.users.length === 0) {
    return (
      <Card>
        <CardHeader><CardTitle className="text-base">KI-Nutzung</CardTitle></CardHeader>
        <CardContent><p className="text-sm text-slate-500">Noch keine Nutzungsdaten.</p></CardContent>
      </Card>
    );
  }

  var t = data.totals || {};
  var sortedUsers = (data.users || []).slice().sort(function cmp(a, b) {
    var va = a[sortKey] || 0;
    var vb = b[sortKey] || 0;
    if (typeof va === 'string') return va.localeCompare(vb) * sortDir;
    return (va - vb) * sortDir;
  });

  var colHeaders = [
    { key: 'name', label: 'Name', cls: 'text-left' },
    { key: 'role', label: 'Rolle', cls: 'text-left' },
    { key: 'total', label: 'Gesamt', cls: 'text-right' },
    { key: 'last_7d', label: '7 Tage', cls: 'text-right' },
    { key: 'last_30d', label: '30 Tage', cls: 'text-right' },
    { key: 'avg_per_active_day', label: '\u00D8/Tag', cls: 'text-right' },
    { key: 'busiest_hour', label: 'Uhrzeit', cls: 'text-center' },
    { key: 'busiest_weekday', label: 'Wochentag', cls: 'text-center' },
    { key: 'last_at', label: 'Zuletzt', cls: 'text-right' },
  ];

  return (
    <Card data-testid="usage-analytics-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <BarChart3 className="h-4 w-4" /> KI-Nutzung
        </CardTitle>
        <CardDescription>Auswertung der Assistenten-Nutzung pro Nutzer und Zeitraum.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Kennzahlen */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <StatCard icon={<Users className="h-4 w-4" />} label="Aktive Nutzer (7 T.)" value={t.users_active_7d} />
          <StatCard icon={<MessageCircle className="h-4 w-4" />} label="Nachrichten (7 T.)" value={t.messages_7d} />
          <StatCard icon={<MessageCircle className="h-4 w-4" />} label="Nachrichten (30 T.)" value={t.messages_30d} />
          <StatCard icon={<MessageCircle className="h-4 w-4" />} label="Gesamt" value={t.messages_total} />
          <StatCard icon={<Clock className="h-4 w-4" />} label={"\u00D8 pro Tag (30 T.)"} value={t.avg_messages_per_day_30d} />
        </div>

        {/* Wochentag + Uhrzeit Balken */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="text-xs font-medium text-slate-600 mb-2">Nachrichten nach Wochentag</div>
            <BarRow items={data.by_weekday} labels={WEEKDAYS} colorClass="bg-indigo-400" />
          </div>
          <div>
            <div className="text-xs font-medium text-slate-600 mb-2">Nachrichten nach Uhrzeit (Berlin)</div>
            <BarRow items={data.by_hour} labels={Array.from({length: 24}, function(_, i) { return i; })} colorClass="bg-sky-400" />
          </div>
        </div>

        {/* Nutzer-Tabelle */}
        <div>
          <div className="text-xs font-medium text-slate-600 mb-2">Nutzung pro Mitarbeiter</div>
          <div className="overflow-x-auto border border-slate-200 rounded-lg">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  {colHeaders.map(function renderTh(col) {
                    var active = sortKey === col.key;
                    return (
                      <th key={col.key}
                          className={"py-2 px-2 font-medium text-slate-600 cursor-pointer hover:text-slate-900 select-none " + col.cls}
                          onClick={function onSort() { handleSort(col.key); }}>
                        {col.label}{active ? (sortDir > 0 ? ' \u25B2' : ' \u25BC') : ''}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {sortedUsers.map(function renderRow(u) {
                  return (
                    <UserRow key={u.user_id} u={u}
                             expanded={expandedUser === u.user_id}
                             onToggle={function toggle() {
                               setExpandedUser(function prev(p) { return p === u.user_id ? null : u.user_id; });
                             }} />
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
